import React from 'react';

import { PERMISSIONS } from 'api/auth/constants';
import { appReactMemo } from 'hocs';
import { PERMISSION_TYPE, useRBAC } from 'hooks/useRBAC';
import { INullable } from 'utils/types';

import { AppLink, GlobalLink, Text } from './styled';

type ILink = {
  children?: React.ReactNode | string;
  className?: string;
  conditionType?: PERMISSION_TYPE;
  disabled?: boolean;
  href?: string;
  linkRef?: React.MutableRefObject<INullable<HTMLAnchorElement>>;
  onClick?: () => void;
  permissions?: PERMISSIONS[];
  target?: string;
  testIds?: { root?: string };
  to?: string;
  variant?: 'bold' | 'regular';
};

const Link = ({
  children,
  className,
  conditionType,
  disabled,
  href = '',
  linkRef,
  onClick,
  permissions,
  target,
  testIds,
  to = '',
  variant = 'regular',
}: ILink) => {
  const onClickLink = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (typeof onClick === 'function') {
        e.preventDefault();
        onClick?.();
      }
    },
    [onClick],
  );

  const { checkPermission } = useRBAC();

  const isCanRedirect = checkPermission({
    conditionType,
    list: permissions,
  });

  if (!children || (permissions && !isCanRedirect)) {
    return (
      <Text data-testid={testIds?.root} size='md' variant='regular'>
        {children || '-'}
      </Text>
    );
  }

  if (href) {
    return (
      <GlobalLink
        className={className}
        data-testid={testIds?.root}
        disabled={disabled}
        href={disabled ? '#' : href}
        onClick={onClickLink}
        ref={linkRef}
        target={target}
        variant={variant}
      >
        {children}
      </GlobalLink>
    );
  }

  return (
    <AppLink
      className={className}
      data-testid={testIds?.root}
      disabled={disabled}
      onClick={onClickLink}
      ref={linkRef}
      target={target}
      to={disabled ? '#' : to}
      variant={variant}
    >
      {children}
    </AppLink>
  );
};

export default appReactMemo(Link);
